import React, { useState, useEffect, useRef } from 'react';
import Editor from '@monaco-editor/react';
import './StreamingScriptEditor.css';

interface StreamingScriptEditorProps {
  initialCode?: string;
  language?: string;
  isStreaming?: boolean;
  onCodeChange?: (code: string) => void;
}

const StreamingScriptEditor = React.forwardRef<
  { appendStreamChunk: (chunk: string) => void },
  StreamingScriptEditorProps
>(({
  initialCode = '',
  language = 'python',
  isStreaming = false,
  onCodeChange
}, ref) => {
  const [code, setCode] = useState(initialCode);
  const [streamBuffer, setStreamBuffer] = useState('');
  const editorRef = useRef<any>(null);
  const lastCursorPositionRef = useRef<{ lineNumber: number; column: number } | null>(null);
  const isUserEditingRef = useRef(false);
  
  useEffect(() => {
    // Only update code if it's different to avoid losing user edits
    if (initialCode !== code) {
      setCode(initialCode);
    }
  }, [initialCode]);
  
  // Handle streaming updates
  const appendStreamChunk = (chunk: string) => {
    if (isUserEditingRef.current) {
      // Queue the chunk if user is editing
      setStreamBuffer(prev => prev + chunk);
      return;
    }
    
    setCode(prev => {
      const newCode = prev + chunk;
      
      // Preserve cursor position during streaming
      if (editorRef.current && lastCursorPositionRef.current) {
        const position = lastCursorPositionRef.current;
        setTimeout(() => {
          editorRef.current?.setPosition(position);
        }, 0);
      }
      
      return newCode;
    });
  };
  
  // Flush buffer when user stops editing
  useEffect(() => {
    if (!isUserEditingRef.current && streamBuffer) {
      setCode(prev => prev + streamBuffer);
      setStreamBuffer('');
    }
  }, [streamBuffer]);
  
  const handleEditorMount = (editor: any) => {
    editorRef.current = editor;
    
    // Track cursor position
    editor.onDidChangeCursorPosition((e: any) => {
      lastCursorPositionRef.current = e.position;
    });
    
    // Track when user is typing
    editor.onDidChangeModelContent((e: any) => {
      if (e.isFlush || e.isRedoing || e.isUndoing) {
        return;
      }
      isUserEditingRef.current = true;
      
      // Reset editing flag after a delay
      setTimeout(() => {
        isUserEditingRef.current = false;
      }, 1000);
    });
  };
  
  const handleCodeChange = (value: string | undefined) => {
    if (value !== undefined) {
      setCode(value);
      onCodeChange?.(value);
    }
  };
  
  // Expose appendStreamChunk method via ref
  React.useImperativeHandle(ref, () => ({
    appendStreamChunk
  }));
  
  return (
    <div className="streaming-script-editor">
      {isStreaming && (
        <div className="streaming-indicator">
          <div className="streaming-dot"></div>
          <span>Generating script...</span>
        </div>
      )}
      
      <Editor
        height="100%"
        language={language}
        value={code}
        onChange={handleCodeChange}
        onMount={handleEditorMount}
        theme="vs-dark"
        options={{
          minimap: { enabled: false },
          fontSize: 14,
          wordWrap: 'on',
          scrollBeyondLastLine: false,
          automaticLayout: true,
          readOnly: isStreaming && !isUserEditingRef.current,
          scrollbar: {
            vertical: 'visible',
            horizontal: 'visible',
            verticalScrollbarSize: 10,
            horizontalScrollbarSize: 10
          }
        }}
      />
    </div>
  );
});

StreamingScriptEditor.displayName = 'StreamingScriptEditor';

export default StreamingScriptEditor;

// Export the append function for external use
export const appendToStreamingEditor = (
  editorRef: React.RefObject<{ appendStreamChunk: (chunk: string) => void }>,
  chunk: string
) => {
  editorRef.current?.appendStreamChunk(chunk);
};